import { FormProvider, useForm } from "react-hook-form";
import { schemas } from "../../generated/serverClient";
import { z } from "zod";
import { BookedQuote } from "../../providers/bookedQuotesProvider";
import { runSafelyWithServerClient } from "../../api/server.api";
import { Form2Theme } from "../../misc/Form2";
import {
  DialogActions,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { MoneyInput } from "../../inputs/MoneyInput";
import { centsToMoney, type Money as MoneyType } from "../../model/Money";
import { LoadingButton } from "@mui/lab";
import { RecordContextProvider, required, TextInput } from "react-admin";
import { Column } from "../../misc/Column";
import { Money } from "../../misc/Money";
import { useId, useMemo } from "react";

type ChangePricesDto = z.infer<typeof schemas.ChangePricesDto>;
/** Same shape as `ChangePricesDto`, but different `newPrices` keys. */
type ChangePricesFormValues = ChangePricesDto;

export function ChangePricesForm(props: {
  bookedQuote: BookedQuote;
  backButton: React.ReactElement;
  onSuccess: () => void;
}) {
  const { bookedQuote, backButton, onSuccess } = props;
  const { oldPrices, defaultValues, labels, keyByLabel, labelByKey } = useMemo(() => {
    const labels = Object.keys(bookedQuote.prices)
      // Do not allow changing the "Grundbetrag", "Lehrmittelgebühr" or the obsolete "Prüfungsgebühr" ...
      .filter((it) => !(it.startsWith("Grund") || it === "Lehrmittelgebühr" || it.startsWith("Prüfungsgebühr")))
      .sort((a, b) => a.localeCompare(b, "de"));
    const keyByLabel: { [label: string]: string } = {};
    const labelByKey: { [key: string]: string } = {};
    const oldPrices: { [key: string]: MoneyType } = {};
    const defaultValues: ChangePricesFormValues = {
      bookedQuoteId: bookedQuote.id,
      newPrices: {},
      reason: "",
    };
    for (const label of labels) {
      const key = _labelToKey(label);
      labelByKey[key] = label;
      keyByLabel[label] = key;
      const oldPrice = centsToMoney(bookedQuote.prices[label]);
      oldPrices[key] = defaultValues.newPrices[key] = oldPrice;
    }
    // Paranoia check: each label should have been mapped to a different key ...
    if (Object.keys(labelByKey).length !== labels.length) {
      throw new Error(
        `Object.keys(labelByKey).length !== labels.length -- labels:\n${JSON.stringify(labels, null, 2)}`,
      );
    }
    return { oldPrices, defaultValues, labels, keyByLabel, labelByKey };
  }, [bookedQuote]);
  const form = useForm<ChangePricesFormValues>({ defaultValues });

  async function changePrices(formValues: ChangePricesFormValues) {
    const dto = {
      ...formValues,
      newPrices: Object.fromEntries(
        Object.entries(formValues.newPrices).map(([key, money]) => [labelByKey[key], money]),
      ),
    };
    await runSafelyWithServerClient(
      async (serverClient) => serverClient.changePrices(dto, { params: { studentId: bookedQuote.studentId } }),
      { onSuccess },
    );
  }

  return (
    <RecordContextProvider value={defaultValues}>
      <FormProvider {...form}>
        <Form2Theme>
          <form onSubmit={form.handleSubmit(changePrices)}>
            <DialogContent>
              <Column spacing="1em">
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell align="right">{"alter\u00A0Preis"}</TableCell>
                        <TableCell />
                        <TableCell align="right">{"neuer\u00A0Preis"}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {labels.map((label) => (
                        <_TableRow key={label} label={label} oldPrice={oldPrices[keyByLabel[label]]} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TextInput
                  label="Änderungsgrund"
                  helperText="Bei einer Preisänderung wird automatisch eine Notiz mit dem hier genannten Grund erstellt."
                  source="reason"
                  validate={required("Bitte gib den Grund für die Preisänderung ein.")}
                />
              </Column>
            </DialogContent>
            <DialogActions>
              {backButton}
              <LoadingButton type="submit" variant="contained" loading={form.formState.isSubmitting}>
                Speichern
              </LoadingButton>
            </DialogActions>
          </form>
        </Form2Theme>
      </FormProvider>
    </RecordContextProvider>
  );
}

function _TableRow(props: { label: string; oldPrice: MoneyType }) {
  const { label, oldPrice } = props;
  const id = useId();
  return (
    <TableRow key={label}>
      <TableCell>
        <label htmlFor={id}>{label}</label>
      </TableCell>
      <TableCell align="right">
        <Money value={oldPrice} />
      </TableCell>
      <TableCell align="right">{"\u00A0⟹\u00A0"}</TableCell>
      <TableCell align="right">
        <MoneyInput
          id={id}
          source={`newPrices.${_labelToKey(label)}`}
          size="small"
          sx={{
            width: "100px",
            minWidth: "100px",
            "& .MuiInputBase-root": { width: "100px", minWidth: "100px" },
            "& .MuiFormHelperText-root": { display: "none" },
          }}
        />
      </TableCell>
    </TableRow>
  );
}

function _labelToKey(label: string): string {
  // Make sure the key does not contain any special characters for a property path ...
  return label.replaceAll(/[.,[\]]/g, "_");
}
